<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { searchAddresses, formatAddress, mapResultToPlaceDetails } from '@/services/tomtomService';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Endereço'
  },
  placeholder: {
    type: String,
    default: 'Digite um endereço'
  },
  required: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  clearable: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  density: {
    type: String,
    default: 'comfortable'
  }
});

const emit = defineEmits(['update:modelValue', 'place-selected', 'input', 'clear', 'copy-text', 'api-error']);

const searchInput = ref(props.modelValue);
const suggestions = ref([]);
const loading = ref(false);
const menuOpen = ref(false);
const selectedPlace = ref(null);
const debounceTimeout = ref(null);
const inputField = ref(null);
const apiError = ref(false);
const errorMessage = ref('');

// Estado para o campo adicional de bairro
const showBairroField = ref(false);
const bairroInput = ref('');
const pendingAddressData = ref(null);

// Computed property para mostrar o menu apenas quando houver sugestões
const showMenu = computed(() => menuOpen.value && suggestions.value.length > 0);

// Nossa implementação de debounce
const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const debouncedSearch = debounce(async (query) => {
  if (!query || query.length < 3) {
    suggestions.value = [];
    menuOpen.value = false;
    return;
  }

  loading.value = true;
  try {
    const response = await searchAddresses(query);
    const results = response.results || [];
    
    if (results.length > 0) {
      suggestions.value = results;
      menuOpen.value = true;
      apiError.value = false;
    } else {
      suggestions.value = [];
      menuOpen.value = false;
    }
  } catch (error) {
    console.error('Erro na busca de endereços:', error);
    apiError.value = true;
    errorMessage.value = error.message || 'Erro ao buscar endereços';
    emit('api-error', { message: errorMessage.value, error });
    suggestions.value = [];
    menuOpen.value = false;
  } finally {
    loading.value = false;
  }
}, 300);

// Função para atualizar o input e emitir eventos
const updateInput = (value) => {
  searchInput.value = value;
  emit('update:modelValue', value);
  emit('input', value);
};

// Função para lidar com a seleção de um endereço
const selectAddress = (result) => {
  menuOpen.value = false;
  
  // Mapear o resultado para o formato esperado
  const details = mapResultToPlaceDetails(result);
  
  // Verificar se o bairro está presente
  if (!details.bairro || details.bairro.trim() === '') {
    // Se não tem bairro, mostrar campo adicional
    pendingAddressData.value = details;
    bairroInput.value = '';
    showBairroField.value = true;
  } else {
    // Se tem bairro, prosseguir normalmente
    selectedPlace.value = details;
    const formattedAddress = formatAddress(result);
    updateInput(formattedAddress);
    emit('place-selected', details);
  }
};

// Função para confirmar o bairro e prosseguir
const confirmarBairro = () => {
  if (bairroInput.value.trim()) {
    // Atualizar o bairro no endereço pendente
    pendingAddressData.value.bairro = bairroInput.value.trim();
    selectedPlace.value = pendingAddressData.value;
    
    // Formatar o endereço completo com o bairro
    const formattedAddress = [
      pendingAddressData.value.logradouro,
      pendingAddressData.value.numero,
      pendingAddressData.value.bairro,
      pendingAddressData.value.cidade,
      pendingAddressData.value.estado,
      pendingAddressData.value.cep
    ].filter(Boolean).join(', ');
    
    updateInput(formattedAddress);
    emit('place-selected', pendingAddressData.value);
    
    // Fechar campo adicional
    showBairroField.value = false;
    pendingAddressData.value = null;
    bairroInput.value = '';
  }
};

// Função para cancelar a confirmação do bairro
const cancelarBairro = () => {
  showBairroField.value = false;
  pendingAddressData.value = null;
  bairroInput.value = '';
};

// Função para limpar o input
const clearInput = () => {
  updateInput('');
  selectedPlace.value = null;
  suggestions.value = [];
  menuOpen.value = false;
  apiError.value = false;
  showBairroField.value = false;
  pendingAddressData.value = null;
  bairroInput.value = '';
  emit('clear');
};

// Função para tratar a entrada do usuário
const onInputChange = () => {
  if (searchInput.value) {
    debouncedSearch(searchInput.value);
  } else {
    suggestions.value = [];
    menuOpen.value = false;
  }
};

// Observar alterações no modelValue (caso seja alterado externamente)
watch(() => props.modelValue, (newVal) => {
  if (newVal !== searchInput.value) {
    searchInput.value = newVal;
  }
});

// Cancelar debounce quando o componente for desmontado
onUnmounted(() => {
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value);
  }
});

// Inicialização
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Fechar o menu quando clicar fora do componente
const handleClickOutside = (event) => {
  const el = inputField.value?.$el;
  const dropdown = document.getElementById(dropdownPortalId);
  if (el && !el.contains(event.target) && dropdown && !dropdown.contains(event.target) && menuOpen.value) {
    menuOpen.value = false;
  }
};

// Função para copiar texto para área de transferência
const copyText = (text, description) => {
  navigator.clipboard.writeText(text).then(() => {
    emit('copy-text', { text: description, value: text });
  }).catch(err => {
    console.error('Erro ao copiar texto:', err);
  });
};

// Portal for the dropdown
const dropdownPortalId = `tomtom-address-autocomplete-dropdown-${Date.now()}`;
let dropdownPortalElement = null;

// Position the dropdown relative to the input field
const positionDropdown = () => {
  if (!showMenu.value || !inputField.value || !dropdownPortalElement) return;

  const inputRect = inputField.value.$el.getBoundingClientRect();

  dropdownPortalElement.style.position = 'fixed';
  dropdownPortalElement.style.width = `${inputRect.width}px`;
  dropdownPortalElement.style.left = `${inputRect.left}px`;
  dropdownPortalElement.style.top = `${inputRect.bottom + window.scrollY}px`;
  dropdownPortalElement.style.maxHeight = '300px';
  dropdownPortalElement.style.overflowY = 'auto';
  dropdownPortalElement.style.zIndex = '999999';
  dropdownPortalElement.style.backgroundColor = 'white';
  dropdownPortalElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
  dropdownPortalElement.style.borderRadius = '8px';
};

// Render suggestions in the dropdown portal
const renderSuggestions = () => {
  if (!dropdownPortalElement) return;

  if (showMenu.value) {
    dropdownPortalElement.innerHTML = '';

    const list = document.createElement('div');
    list.className = 'suggestions-list';

    suggestions.value.forEach((suggestion) => {
      const item = document.createElement('div');
      item.className = 'suggestion-item';

      // Container principal
      const contentDiv = document.createElement('div');
      contentDiv.className = 'suggestion-content';

      // Linha principal: Logradouro + Número
      const mainLine = document.createElement('div');
      mainLine.className = 'suggestion-main-line';
      const logradouro = suggestion.logradouro || '';
      const numero = suggestion.numero || '';
      mainLine.textContent = [logradouro, numero].filter(Boolean).join(', ');

      // Linha secundária: Bairro, Cidade - Estado
      const secondaryLine = document.createElement('div');
      secondaryLine.className = 'suggestion-secondary-line';
      const bairro = suggestion.bairro || '';
      const cidade = suggestion.cidade || '';
      const estado = suggestion.estado || '';
      const locationParts = [bairro, cidade, estado].filter(Boolean);
      secondaryLine.textContent = locationParts.join(', ');

      // Linha do CEP
      const cepLine = document.createElement('div');
      cepLine.className = 'suggestion-cep-line';
      const cep = suggestion.cep || '';
      if (cep) {
        cepLine.textContent = `CEP: ${cep}`;
      }

      // Provider
      const providerDiv = document.createElement('div');
      providerDiv.className = 'suggestion-provider';
      providerDiv.textContent = suggestion.provider || 'leaflet';

      contentDiv.appendChild(mainLine);
      if (secondaryLine.textContent) contentDiv.appendChild(secondaryLine);
      if (cepLine.textContent) contentDiv.appendChild(cepLine);

      item.appendChild(contentDiv);
      item.appendChild(providerDiv);

      item.addEventListener('click', () => {
        selectAddress(suggestion);
      });

      list.appendChild(item);
    });

    if (loading.value && suggestions.value.length === 0) {
      const loadingItem = document.createElement('div');
      loadingItem.className = 'suggestion-loading';
      loadingItem.textContent = 'Buscando...';
      list.appendChild(loadingItem);
    }

    dropdownPortalElement.appendChild(list);
    dropdownPortalElement.style.display = 'block';
    positionDropdown();
  } else {
    dropdownPortalElement.style.display = 'none';
  }
};

// Watchers for rendering suggestions
watch(showMenu, () => renderSuggestions());
watch(suggestions, () => renderSuggestions(), { deep: true });
watch(loading, () => renderSuggestions());

// Create and remove the dropdown portal
onMounted(() => {
  dropdownPortalElement = document.createElement('div');
  dropdownPortalElement.id = dropdownPortalId;
  dropdownPortalElement.style.display = 'none';
  document.body.appendChild(dropdownPortalElement);

  const style = document.createElement('style');
  style.textContent = `
    #${dropdownPortalId} .suggestion-item {
      padding: 12px 16px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }
    #${dropdownPortalId} .suggestion-item:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    #${dropdownPortalId} .suggestion-item:last-child {
      border-bottom: none;
    }
    #${dropdownPortalId} .suggestion-content {
      flex: 1;
      min-width: 0;
    }
    #${dropdownPortalId} .suggestion-main-line {
      font-weight: 500;
      font-size: 0.875rem;
      color: rgba(0, 0, 0, 0.87);
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    #${dropdownPortalId} .suggestion-secondary-line {
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
      margin-bottom: 2px;
    }
    #${dropdownPortalId} .suggestion-cep-line {
      font-size: 0.7rem;
      color: rgba(0, 0, 0, 0.5);
      font-weight: 500;
    }
    #${dropdownPortalId} .suggestion-provider {
      font-size: 0.625rem;
      color: rgba(0, 0, 0, 0.4);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-left: 12px;
      flex-shrink: 0;
      align-self: flex-end;
      padding-bottom: 2px;
    }
    #${dropdownPortalId} .suggestion-loading {
      padding: 12px 16px;
      text-align: center;
      font-style: italic;
      color: rgba(0, 0, 0, 0.6);
    }
  `;
  document.head.appendChild(style);

  window.addEventListener('resize', positionDropdown);
  window.addEventListener('scroll', positionDropdown, true);
});

onUnmounted(() => {
  if (dropdownPortalElement) {
    document.body.removeChild(dropdownPortalElement);
  }
  window.removeEventListener('resize', positionDropdown);
  window.removeEventListener('scroll', positionDropdown, true);
});
</script>

<template>
  <div class="tomtom-address-autocomplete">
    <v-text-field
      ref="inputField"
      v-model="searchInput"
      :label="label"
      :placeholder="apiError ? 'API não configurada' : placeholder"
      :variant="variant"
      :density="density"
      :clearable="clearable && !apiError"
      :disabled="disabled || apiError"
      :required="required"
      :loading="loading && !apiError"
      @click:clear="clearInput"
      @focus="menuOpen = true"
      @input="onInputChange"
      prepend-inner-icon="mdi-map-search"
      hide-details="auto"
      autocomplete="off"
      :error="apiError"
      :error-messages="apiError ? errorMessage : ''"
    >
      <template v-slot:append>
        <v-progress-circular
          v-if="loading && !apiError"
          indeterminate
          size="20"
          width="2"
          color="primary"
        ></v-progress-circular>
        <v-tooltip v-if="apiError" location="bottom">
          <template v-slot:activator="{ props }">
            <v-icon
              color="error"
              v-bind="props"
              icon="mdi-alert-circle"
            ></v-icon>
          </template>
          <span>{{ errorMessage }}</span>
        </v-tooltip>
      </template>
    </v-text-field>
    
    <!-- Alerta de erro da API TomTom -->
    <div v-if="apiError" class="error-compact mt-2">
      <v-alert
        type="error"
        variant="tonal"
        density="compact"
        icon="mdi-map-marker-alert"
      >
        <template v-slot:title>
          <span class="text-caption">{{ errorMessage }}</span>
        </template>
      </v-alert>
    </div>

    <!-- Campo adicional para confirmação do bairro -->
    <v-expand-transition>
      <div v-if="showBairroField" class="bairro-field mt-3">
        <v-text-field
          v-model="bairroInput"
          label="Qual o bairro desta localização?"
          placeholder="Digite o bairro"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-map-marker"
          :error="!bairroInput.trim()"
          :error-messages="!bairroInput.trim() ? ['Bairro é obrigatório para continuar'] : []"
          @keyup.enter="confirmarBairro"
          hide-details="auto"
        >
          <template v-slot:append>
            <v-btn
              icon="mdi-check"
              color="info"
              :disabled="!bairroInput.trim()"
              @click="confirmarBairro"
              title="Confirmar bairro"
              class="mr-2"
            ></v-btn>
            <v-btn
              icon="mdi-close"
              color="grey"
              @click="cancelarBairro"
              title="Cancelar"
            ></v-btn>
          </template>
        </v-text-field>
        <div class="text-caption text-grey mt-1">
          <v-icon size="small" class="mr-1">mdi-information</v-icon>
          O bairro não foi identificado automaticamente. Por favor, confirme para continuar.
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped>
.tomtom-address-autocomplete {
  position: relative;
  width: 100%;
}

.error-compact {
  border-radius: 4px;
  overflow: hidden;
}

.bairro-field {
  border-left: 3px solid rgb(var(--v-theme-primary));
  padding-left: 12px;
  background-color: rgba(var(--v-theme-primary), 0.05);
  border-radius: 4px;
  padding: 12px;
}

.suggestion-content {
  display: flex;
  flex-direction: column;
}

.suggestion-main-line {
  font-weight: 500;
  font-size: 0.875rem;
}

.suggestion-secondary-line {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.suggestion-cep-line {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.suggestion-provider {
  font-size: 0.625rem;
  color: rgba(0, 0, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
  text-align: right;
}
</style>
